import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import InstructionAttachmentThumbnails, {
  type InstructionAttachment,
} from '@/components/feature/InstructionAttachmentThumbnails';
import { useClientChangeOrders, type ClientChangeOrder } from '@/hooks/useClientChangeOrders';
import { formatClientFacingPrice } from '@/lib/changeOrderPricingDisplay';

interface Props {
  projectId: string | null;
  compact?: boolean;
}

function fmtDate(iso: string, locale: string): string {
  return new Date(iso).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function toAttachments(files: ClientChangeOrder['files']): InstructionAttachment[] {
  return files.map((f) => ({
    id: f.id,
    file_url: f.file_url,
    file_name: f.file_name,
    file_type: f.file_type,
    photo_type: null,
    caption: null,
    is_client_visible: true,
    is_subcontractor_visible: null,
  }));
}

export default function ClientChangeOrdersPanel({ projectId, compact = false }: Props) {
  const { t } = useTranslation();
  const { changeOrders, loading, error, submitResponse, pendingCount } = useClientChangeOrders(projectId);
  const locale = t('date.locale') || 'en-NZ';

  const [actionId, setActionId] = useState<string | null>(null);
  const [responseText, setResponseText] = useState('');
  const [actionType, setActionType] = useState<'approve' | 'decline' | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function getStatusConfig(status: string) {
    const configs: Record<string, { label: string; color: string; bg: string; icon: string }> = {
      pending: { label: t('client.status.pending'), color: '#E8621A', bg: '#FEF0E7', icon: 'ri-time-line' },
      sent: { label: t('client.status.sent'), color: '#0891B2', bg: '#ECFEFF', icon: 'ri-send-plane-line' },
      in_progress: { label: t('client.status.inProgress'), color: '#E8621A', bg: '#FEF0E7', icon: 'ri-loader-2-line' },
      approved: { label: t('client.status.approved'), color: '#059669', bg: '#F0FDF4', icon: 'ri-check-line' },
      client_approved: { label: t('client.status.clientApproved'), color: '#059669', bg: '#F0FDF4', icon: 'ri-check-line' },
      client_rejected: { label: t('client.status.clientDeclined'), color: '#EF4444', bg: '#FEF2F2', icon: 'ri-close-line' },
      rejected: { label: t('client.status.declined'), color: '#EF4444', bg: '#FEF2F2', icon: 'ri-close-line' },
      sent_to_client: { label: t('client.status.awaitingApproval'), color: '#E8621A', bg: '#FEF0E7', icon: 'ri-time-line' },
      pending_client_approval: { label: t('client.status.awaitingApproval'), color: '#E8621A', bg: '#FEF0E7', icon: 'ri-time-line' },
      completed: { label: t('client.status.completed'), color: '#059669', bg: '#F0FDF4', icon: 'ri-check-double-line' },
      closed: { label: t('client.status.closed'), color: '#6B7280', bg: '#F9FAFB', icon: 'ri-lock-line' },
      cancelled: { label: 'Cancelled', color: '#DC2626', bg: '#FEF2F2', icon: 'ri-forbid-2-line' },
    };
    return configs[status?.toLowerCase() ?? ''] || configs.pending;
  }

  function isPendingStatus(status: string): boolean {
    const s = status.toLowerCase();
    return (
      s === 'pending' ||
      s === 'sent' ||
      s === 'sent_to_client' ||
      s === 'pending_client_approval' ||
      s === 'in_progress'
    );
  }

  function getApprovalLabel(co: ClientChangeOrder): string {
    if (co.client_decision === 'approved' || co.status === 'client_approved' || co.status === 'approved') {
      return t('client.changeOrders.approvalApproved');
    }
    if (co.client_decision === 'declined' || co.status === 'client_rejected' || co.status === 'rejected') {
      return t('client.changeOrders.approvalDeclined');
    }
    if (co.requires_client_approval && isPendingStatus(co.status)) {
      return t('client.changeOrders.approvalPending');
    }
    return t('client.changeOrders.approvalNotRequired');
  }

  async function handleSubmit(id: string, action: 'approved' | 'declined') {
    setSubmitting(true);
    const ok = await submitResponse(id, action, responseText);
    if (ok) {
      setActionId(null);
      setActionType(null);
      setResponseText('');
    }
    setSubmitting(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <i className="ri-loader-4-line animate-spin text-[#E8621A] text-2xl"></i>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#EF4444]">
          <i className="ri-error-warning-line text-5xl"></i>
        </div>
        <p className="text-[#374151] font-semibold text-sm">{t('client.changeOrders.loadError')}</p>
        {!compact && (
          <p className="text-[#9CA3AF] text-xs mt-1 max-w-xs mx-auto">{error}</p>
        )}
      </div>
    );
  }

  if (changeOrders.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
          <i className="ri-exchange-line text-5xl"></i>
        </div>
        <p className="text-[#374151] font-semibold text-sm">{t('client.changeOrders.empty')}</p>
        {!compact && (
          <p className="text-[#9CA3AF] text-xs mt-1 max-w-xs mx-auto">{t('client.changeOrders.emptyDesc')}</p>
        )}
      </div>
    );
  }

  return (
    <div>
      {!compact && pendingCount > 0 && (
        <div className="flex items-center justify-end mb-4">
          <span className="bg-[#E8621A] text-white text-xs font-bold px-3 py-1 rounded-full">
            {t('client.instructions.pending', { count: pendingCount })}
          </span>
        </div>
      )}

      <div className="space-y-4">
        {changeOrders.map((co) => {
          const statusCfg = getStatusConfig(co.status);
          const isPendingApproval = co.requires_client_approval && isPendingStatus(co.status);
          const isActionOpen = actionId === co.id;
          const displayDate = co.updated_at || co.created_at;

          return (
            <div
              key={co.id}
              className={`bg-white rounded-2xl border overflow-hidden ${isPendingApproval ? 'border-[#E8621A]/20' : 'border-gray-100'}`}
            >
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  {co.co_number && (
                    <span className="text-[10px] font-bold text-[#E8621A] bg-[#FEF0E7] px-2 py-0.5 rounded-lg tracking-wide flex-shrink-0">
                      {co.co_number}
                    </span>
                  )}
                  <div className="flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: statusCfg.bg }}>
                    <i className={`${statusCfg.icon} text-[10px]`} style={{ color: statusCfg.color }}></i>
                    <span className="text-[10px] font-bold" style={{ color: statusCfg.color }}>{statusCfg.label}</span>
                  </div>
                  <span className="text-[10px] font-medium text-[#6B7280] bg-[#F9FAFB] px-2 py-0.5 rounded-full">
                    {getApprovalLabel(co)}
                  </span>
                  <span className="text-[#D1D5DB] text-[10px] ml-auto">{fmtDate(displayDate, locale)}</span>
                </div>

                <h4 className="text-[#1A1A1A] text-[15px] font-bold leading-snug mb-2">{co.title}</h4>

                {co.reason && (
                  <p className="text-[#6B7280] text-sm leading-relaxed mb-2">
                    <span className="font-semibold">{t('client.instructions.reason')}</span> {co.reason}
                  </p>
                )}

                {co.client_description && (
                  <p className="text-[#6B7280] text-sm leading-relaxed whitespace-pre-line">{co.client_description}</p>
                )}

                {(co.current_finish_description || co.new_finish_description) && (
                  <div className="mt-2 space-y-1">
                    {co.current_finish_description && (
                      <p className="text-xs text-[#374151]">
                        <span className="font-semibold">{t('client.instructions.currentFinish')}</span>{' '}
                        {co.current_finish_description}
                      </p>
                    )}
                    {co.new_finish_description && (
                      <p className="text-xs text-[#374151]">
                        <span className="font-semibold">{t('client.instructions.newFinish')}</span>{' '}
                        {co.new_finish_description}
                      </p>
                    )}
                  </div>
                )}

                {(co.client_price != null || co.schedule_impact) && (
                  <div className="mt-3 flex gap-3 flex-wrap">
                    {co.client_price != null && (() => {
                      const price = formatClientFacingPrice(co.client_price, {
                        title: co.title,
                        reason: co.reason,
                      });
                      return (
                      <span className={`text-xs px-2.5 py-1 rounded-lg border ${
                        price.isCredit
                          ? 'text-[#7C3AED] bg-[#F5F3FF] border-[#DDD6FE]'
                          : 'text-[#374151] bg-[#F9FAFB] border-gray-100'
                      }`}>
                        <i className={`${price.isCredit ? 'ri-indeterminate-circle-line' : 'ri-money-dollar-circle-line'} text-[10px] mr-1`}></i>
                        {price.isCredit ? 'Client Credit' : t('client.instructions.price')}{' '}
                        <span className="font-semibold">{price.amountText}</span>
                      </span>
                      );
                    })()}
                    {co.schedule_impact && (
                      <span className="text-xs text-[#374151] bg-[#F9FAFB] px-2.5 py-1 rounded-lg border border-gray-100">
                        <i className="ri-calendar-line text-[10px] mr-1"></i>
                        {t('client.instructions.schedule')}{' '}
                        <span className="font-semibold">{co.schedule_impact}</span>
                      </span>
                    )}
                  </div>
                )}

                {co.files.length > 0 && (
                  <div className="mt-3">
                    <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">
                      {t('client.changeOrders.attachments')}
                    </p>
                    <InstructionAttachmentThumbnails files={toAttachments(co.files)} maxVisible={6} />
                  </div>
                )}

                {co.client_comment && (
                  <div className="mt-3 bg-[#F9FAFB] rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <i className="ri-user-line text-[10px] text-[#374151]"></i>
                      <span className="text-[10px] font-bold text-[#374151]">{t('client.instructions.yourResponse')}</span>
                      {co.client_decided_at && (
                        <span className="text-[#D1D5DB] text-[10px]">{fmtDate(co.client_decided_at, locale)}</span>
                      )}
                    </div>
                    <p className="text-[#374151] text-sm">{co.client_comment}</p>
                  </div>
                )}

                {isPendingApproval && (
                  <div className="mt-4">
                    {!isActionOpen ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setActionId(co.id); setActionType('approve'); setResponseText(''); }}
                          className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#F0FDF4] text-[#059669] text-sm font-semibold cursor-pointer hover:bg-[#DCFCE7] transition-colors whitespace-nowrap border border-[#BBF7D0]"
                        >
                          <i className="ri-check-line"></i>
                          {t('client.instructions.approve')}
                        </button>
                        <button
                          onClick={() => { setActionId(co.id); setActionType('decline'); setResponseText(''); }}
                          className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#FEF2F2] text-[#EF4444] text-sm font-semibold cursor-pointer hover:bg-[#FEE2E2] transition-colors whitespace-nowrap border border-[#FECACA]"
                        >
                          <i className="ri-close-line"></i>
                          {t('client.instructions.decline')}
                        </button>
                      </div>
                    ) : (
                      <div className="bg-[#F9FAFB] rounded-xl p-4 space-y-3">
                        <p className="text-[#1A1A1A] text-sm font-semibold">
                          {actionType === 'approve'
                            ? t('client.instructions.approveChangeOrder')
                            : t('client.instructions.declineChangeOrder')}
                        </p>
                        <textarea
                          value={responseText}
                          onChange={(e) => setResponseText(e.target.value.slice(0, 500))}
                          placeholder={t('client.instructions.optionalComment')}
                          rows={2}
                          className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#374151] placeholder-[#9CA3AF] focus:outline-none focus:border-[#E8621A] transition-colors resize-none"
                        />
                        <div className="flex items-center justify-between">
                          <span className="text-[#D1D5DB] text-[10px]">{responseText.length}/500</span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => { setActionId(null); setActionType(null); setResponseText(''); }}
                              className="px-3 py-2 rounded-lg border border-gray-200 text-[#374151] text-xs cursor-pointer whitespace-nowrap"
                            >
                              {t('client.instructions.cancel')}
                            </button>
                            <button
                              onClick={() => handleSubmit(co.id, actionType === 'approve' ? 'approved' : 'declined')}
                              disabled={submitting}
                              className={`px-3 py-2 rounded-lg text-white text-xs font-semibold cursor-pointer whitespace-nowrap disabled:opacity-40 ${actionType === 'approve' ? 'bg-[#059669]' : 'bg-[#EF4444]'}`}
                            >
                              {submitting ? (
                                <i className="ri-loader-4-line animate-spin"></i>
                              ) : actionType === 'approve' ? (
                                t('client.instructions.confirmApproval')
                              ) : (
                                t('client.instructions.confirmDecline')
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
